import { GetWSMgr, ReRenderCanvas } from "../../UI/src/Utils/StateLib";
import { createClickableToast } from "../../UI/src/appComponents/Blocks/Cards/ToastContainer/utils/createClickableToast";
import { enumObjectTypes, kEmployeeID, kEmployeePositionID } from "../constants";
import { FormatPositionLabel } from "./FormatPositionLabel";
import { sanitizeBlockedValue } from "./FormatFieldValue";
import { isInTabularView } from "./isInTabularView";

export function createEmployeeClickHandler(employeeName: string | null) {
  return (e: Event) => {
    e.stopPropagation();
    const wsMgr = GetWSMgr();
    wsMgr?.BroadcastTopic("OpenEmployeeList", {
      employeeName: employeeName
    });
  };
}

export function createPositionClickHandler(positionId: string | null) {
  return (e: Event) => {
    e.stopPropagation();
    if (!positionId) return;
    const wsMgr = GetWSMgr();
    if (isInTabularView()) {
      wsMgr?.BroadcastTopic("highlightPlanningTableRow", { positionId });
    } else {
      wsMgr?.DoNavToBox({ targetID: positionId }, true, undefined, true, false);
      ReRenderCanvas();
    }
  };
}

export function createEmployeeSubrowClickHandler(
  employeeId: string | null,
  positionId: string | null
) {
  return (e: Event) => {
    e.stopPropagation();
    if (!employeeId || !positionId) return;
    const wsMgr = GetWSMgr();
    wsMgr?.BroadcastTopic("highlightPlanningTableSubrow", { employeeId, positionId });
  };
}

export function createEmployeePositionClickableToast(
  template: string,
  employeeName: string | null,
  positionLabel: string,
  positionId: string | null,
  employeeId?: string | null
) {
  const handlePositionClick = createPositionClickHandler(positionId);
  const handlers: Record<string, EventListener> = { position: handlePositionClick };
  handlers.employee = (e: Event) => {
    if (isInTabularView()) {
      createEmployeeSubrowClickHandler(employeeId ?? null, positionId)(e);
    } else {
      createEmployeeClickHandler(employeeName)(e);
    }
  };

  return createClickableToast({
    template,
    key: ["employee", "position"],
    value: {
      employee: employeeName || "",
      position: positionLabel
    },
    options: {
      keySpecificHandlers: handlers
    },
    textProps: { colorModifier: "success" }
  } as any);
}

export function createPositionClickableToast(
  template: string,
  positionObj: any,
  key: any = "position"
) {
  const positionLabel = FormatPositionLabel(positionObj);
  const positionId = positionObj.SmartGetFieldValue("PositionID");
  const handlePositionClick = createPositionClickHandler(positionId);

  return createClickableToast({
    template,
    key: key,
    value: {
      [key]: positionLabel
    },
    options: {
      keySpecificHandlers: {
        [key]: handlePositionClick
      }
    },
    textProps: { colorModifier: "success" }
  } as any);
}

export function createMovedFromToPositionClickableToast(
  template: string,
  fromPositionObj: any,
  toPositionObj: any
) {
  const fromPositionLabel = fromPositionObj ? FormatPositionLabel(fromPositionObj) : "";
  const fromPositionId = fromPositionObj?.SmartGetFieldValue("PositionID");
  const handleFromPositionClick = createPositionClickHandler(fromPositionId);

  const toPositionLabel = FormatPositionLabel(toPositionObj);
  const toPositionId = toPositionObj.SmartGetFieldValue("PositionID");
  const handleToPositionClick = createPositionClickHandler(toPositionId);

  return createClickableToast({
    template,
    key: ["fromPosition", "toPosition"],
    value: {
      fromPosition: fromPositionLabel,
      toPosition: toPositionLabel
    },
    options: {
      keySpecificHandlers: {
        fromPosition: handleFromPositionClick,
        toPosition: handleToPositionClick
      }
    },
    textProps: { colorModifier: "success" }
  } as any);
}

export function createEntityUpdatedClickableToast(
  dataObj: any,
  clickableToastProps: any = {},
  newPositionId: string | null = null
) {
  const objType = dataObj?.InferObjectType() ?? "Holder";
  if (objType === enumObjectTypes.position) {
    const entityLabel = FormatPositionLabel(dataObj);
    const positionId = dataObj.SmartGetFieldValue("PositionID");
    const handlePositionClick = createPositionClickHandler(positionId);

    const { template, key, value, options, textProps } = clickableToastProps;

    return createClickableToast({
      template: template || "Entity has been updated",
      key: key ?? "entity",
      value: {
        ...value,
        entity: entityLabel,
        targetID: positionId
      },
      options: {
        ...options,
        keySpecificHandlers: {
          entity: handlePositionClick,
          ...options?.keySpecificHandlers
        }
      },
      textProps: { colorModifier: "success", ...textProps }
    } as any);
  } else {
    // Handle employee/holder updates
    const employeeName = sanitizeBlockedValue(dataObj.SmartGetFieldValue("Name"));
    const employeeId = dataObj.SmartGetFieldValue(kEmployeeID);
    const positionId = newPositionId ?? dataObj.SmartGetFieldValue(kEmployeePositionID);
    const handleEntityClick = (e: Event) => {
      if (isInTabularView()) {
        createEmployeeSubrowClickHandler(employeeId, positionId)(e);
      } else {
        createEmployeeClickHandler(employeeName)(e);
      }
    };

    return createClickableToast({
      template: "Entity has been updated",
      key: "entity",
      value: {
        entity: employeeName
      },
      options: {
        keySpecificHandlers: {
          entity: handleEntityClick
        }
      },
      textProps: { colorModifier: "success" }
    } as any);
  }
}

export function createMultipleEmployeesPositionClickableToast(
  template: string,
  count: number,
  positionLabel: string,
  positionId: string | null
) {
  const handlePositionClick = createPositionClickHandler(positionId);

  return createClickableToast({
    template,
    key: ["position"],
    value: {
      count: count.toString(),
      employees: "employees",
      position: positionLabel
    },
    options: {
      keySpecificHandlers: {
        position: handlePositionClick
      }
    },
    textProps: { colorModifier: "success" }
  } as any);
}
